// Single source of truth for "which project are we loading?" — used by
// every editor surface (canvas, preview, future embeds). Resolves in
// priority order:
//
//   1. Explicit URL params: `?owner=…&key=…[&env=…&branch=…&version=…]`
//   2. Preview-subdomain hostname: `{owner}--{key}.preview.symbols.app`
//      (or `…preview.localhost` for local dev)
//   3. Edit-domain pathname: `edit.symbols.app/{owner}/{key}/…`
//   4. Canvas-style pathname: `/{owner}/{key}/…` (excluding reserved
//      chrome routes like `/dashboard`, `/signin`, etc.)
//
// Each source is tried independently — the first non-empty value wins
// per field. Callers can pass their own `reservedPathSegments` set when
// their surface has different chrome routes from canvas; default covers
// the current canvas set.

const DEFAULT_RESERVED_PATHS = new Set([
  'workspaces',
  'dashboard',
  'signin',
  'signup',
  'signup-confirmation',
  'reset-password',
  'waitlist'
])

// Matches the preview-host shapes:
//   {owner}--{key}.preview.symbols.app           (prod)
//   {owner}--{key}.<tier>.preview.symbols.app    (tier — next/dev/staging/upcoming/...)
//   {owner}--{key}.preview.localhost             (local)
//   {owner}--{key}.<tier>.preview.localhost      (local tier — rare)
// The optional `<tier>.` segment lives between the project slug and the
// literal `preview.` apex, so each tier can serve its own preview surface
// without tier-specific URL rewriters.
const PREVIEW_SUBDOMAIN_RE = /^([^.]+)--([^.]+)(?:\.[a-z0-9-]+)?\.preview\.(symbols\.app|localhost)$/
const EDIT_DOMAIN = 'edit.symbols.app'

function fromParams (params) {
  return {
    owner: params.get('owner') || null,
    key: params.get('key') || null,
    env: params.get('env') || null,
    branch: params.get('branch') || null,
    version: params.get('version') || null
  }
}

function fromHost (hostname) {
  const match = hostname.match(PREVIEW_SUBDOMAIN_RE)
  if (match) return { owner: match[1], key: match[2] }
  return {}
}

function fromPath (hostname, pathname, reserved) {
  const segs = pathname.split('/').filter(Boolean)
  if (!segs.length) return {}

  // `edit.symbols.app` uses the first two path segments regardless of
  // reservation (it's a single-purpose host for the editor).
  if (hostname === EDIT_DOMAIN) {
    if (segs.length >= 2) return { owner: segs[0], key: segs[1] }
    return {}
  }

  // Canvas-style: first two segments unless the first is a reserved
  // chrome route (dashboard / signin / etc.).
  if (segs.length >= 2 && !reserved.has(segs[0])) {
    return { owner: segs[0], key: segs[1] }
  }
  return {}
}

export function resolveProjectParams ({
  loc = typeof window !== 'undefined' ? window.location : null,
  reservedPathSegments = DEFAULT_RESERVED_PATHS
} = {}) {
  if (!loc) {
    return { owner: null, key: null, env: null, branch: null, version: null, sources: {} }
  }

  const params = new URLSearchParams(loc.search)
  const p = fromParams(params)
  const h = fromHost(loc.hostname || '')
  const pa = fromPath(loc.hostname || '', loc.pathname || '', reservedPathSegments)

  return {
    owner: p.owner || h.owner || pa.owner || null,
    key: p.key || h.key || pa.key || null,
    env: p.env || null,
    branch: p.branch || null,
    version: p.version || null,
    sources: { params: p, host: h, path: pa }
  }
}

export { DEFAULT_RESERVED_PATHS }
